/**
 * NewEvents — the BD home's "Upcoming Events" cards: events a BD can nominate to right now, then
 * events in the next month that take no nominations (see upcomingEvents for which and in what
 * order). A card for an event without nominations says so instead of offering Nominate Now.
 *
 * BD-only, and the reason it exists: a marketer's entry point to an event is the calendar,
 * but a BD's question is narrower — "which events can I nominate to, and by when". The
 * calendar answers that badly, because an open nomination window has nothing to do with
 * where the event sits in the month.
 *
 * Membership is the same predicate the "Open for Nomination" count uses, and deliberately
 * so: a card here and that number disagreeing would leave nobody able to say which is
 * right. Both live in nominatableEvents.
 *
 * Presentational — the page fetches and decides what clicking does, as with the calendar.
 */

import Button from '@amzn/awsui-components-react/polaris/button';
import Icon from '@amzn/awsui-components-react/polaris/icon';
import Link from '@amzn/awsui-components-react/polaris/link';
import type { Event } from '../../client/EventCalendarClient';
import { typeMetaOf } from '../../utils/eventTaxonomy';
import './NewEvents.css';
import { EventTypeIcon } from '../EventTypeIcon';
import { UserAvatar } from '../UserAvatar';

/** `10/13/2026-10/15/2026`, the form the cards use — not the calendar's YYYY/MM/DD. */
function cardRange(start: Date | undefined, end: Date | undefined): string {
  if (!start) return '—';
  const us = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  if (!end || end.getTime() === start.getTime()) return us(start);
  return `${us(start)}-${us(end)}`;
}

export interface NewEventsProps {
  /** Already narrowed to what a BD can nominate to — see nominatableEvents. */
  events: readonly Event[];
  onViewDetail: (event: Event) => void;
  onNominate: (event: Event) => void;
}

export default function NewEvents({
  events,
  onViewDetail,
  onNominate,
}: NewEventsProps) {
  return (
    /* A scrolling region, so it is focusable and named: keyboard users scroll it with the
       arrow keys once it has focus. */
    <div className="eh-newevents" role="region" aria-label="Events open for nomination" tabIndex={0}>
      {events.map((event) => {
        const meta = typeMetaOf(event);
        return (
          <div key={event.eventId} className="eh-newevents__card">
              <div className="eh-newevents__title">
                {event.eventType ? <EventTypeIcon type={event.eventType} /> : null}
                <span className="eh-newevents__name">{event.eventName || '(untitled)'}</span>
              </div>

              <div className="eh-newevents__facts">
                <span>活动时间: {cardRange(event.startDate, event.endDate)}</span>
                <span>活动地点：{event.city || '—'}</span>
                <Link
                  href={`/event/events/${event.eventId}`}
                  onFollow={(e) => {
                    e.preventDefault();
                    onViewDetail(event);
                  }}
                >
                  View Details
                </Link>
              </div>

              <div className="eh-newevents__facts eh-newevents__facts--meta">
                {meta ? (
                  /* Tinted by type, matching the icon above it — as the Figma's badges are. */
                  <span className="eh-newevents__type" style={{ background: meta.tint }}>
                    {meta.label}
                  </span>
                ) : null}
                {/* Login and badge photo. The design also shows a display name, but an Event
                    carries only the owner's login, so the name is left out. */}
                <span className="eh-newevents__creator">
                  Creater:
                  {event.eventOwner ? (
                    <>
                      <UserAvatar login={event.eventOwner} />
                      <span>{event.eventOwner}</span>
                    </>
                  ) : (
                    ' —'
                  )}
                </span>
              </div>

              <div className="eh-newevents__footer">
                {/* The deadline is the point of this card: it is why the event is here now
                    and gone later, so it carries an icon and the accent colour. */}
                {event.bdNominationSupported ? (
                  <>
                    <span className="eh-newevents__window">
                      <Icon name="status-info" size="small" />{' '}
                      提名时间: {cardRange(event.nominationStartDate, event.nominationEndDate)}
                    </span>
                    <span className="eh-newevents__action">
                      <Button onClick={() => onNominate(event)}>Nominate Now</Button>
                    </span>
                  </>
                ) : (
                  <span className="eh-newevents__window eh-newevents__window--none">无需提名</span>
                )}
              </div>
          </div>
        );
      })}
    </div>
  );
}
